import type { GatewayDispatch } from '@neosocial/protocol';
import { create } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import type { GatewayStatus } from '../lib/gateway/client';
import { applyDispatch, emptyData, expireTyping, type DataState } from './reducer';
import { isIgnored } from './um';

export interface DataStore extends DataState {
  /** The gateway's own status (honest: "ready" only after READY/RESUMED). */
  connection: GatewayStatus;
}

export const useData = create<DataStore>(() => ({ ...emptyData(), connection: 'idle' }));

/**
 * For selectors that build an array or object: compares the result
 * shallowly, so a new array of the same store items doesn't re-render (or
 * loop, under React 19's useSyncExternalStore).
 */
export function useDataShallow<T>(selector: (s: DataStore) => T): T {
  return useData(useShallow(selector));
}

/** Feeds one gateway dispatch into the store. */
export function feed(frame: GatewayDispatch): void {
  useData.setState((s) => applyDispatch(s, frame, Date.now(), isIgnored));
}

export function resetData(): void {
  useData.setState({ ...emptyData(), connection: 'idle' }, true);
}

export function setConnection(c: GatewayStatus): void {
  useData.setState({ connection: c });
}

export function updateData(fn: (s: DataState) => DataState): void {
  useData.setState((s) => fn(s));
}

// typing indicators end on their own
if (typeof window !== 'undefined') {
  window.setInterval(() => {
    const s = useData.getState();
    if (!Object.keys(s.typing).length) return;
    const next = expireTyping(s, Date.now());
    if (next !== s) useData.setState({ typing: next.typing });
  }, 1000);
}
